import { AbstractService } from '@/services/core/serviceFactory';
import { usersGraphqlService } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';

export interface CareSpecialist {
  care_specialist_id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  clinic_names: string;
  clinic_ids: number[];
  deferred_task: boolean;
  follow_up: boolean;
}

export interface CareSpecialistsResponse {
  pep_vw_care_specialist_list: CareSpecialist[];
}

export interface CareSpecialistStatusResponse {
  pep_vw_care_specialist_list: Array<{
    deferred_task: boolean;
    follow_up: boolean;
    care_specialist_id: number;
  }>;
}

export interface CareSpecialistAssignmentsResponse {
  pep_care_specialist_active_assignments: Array<{
    clinic_ids: number[];
  }>;
}

export interface CreateCareSpecialistPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  clinic_ids: number[];
  deferred_task: string;
  follow_up_task: string;
}

export interface CreateCareSpecialistResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface UpdateCareSpecialistPayload {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  clinic_ids: number[];
  deferred_task: boolean;
  follow_up_task: boolean;
}

export interface UpdateCareSpecialistResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export class CareSpecialistService extends AbstractService {
  /**
   * Fetch all care specialists
   */
  async fetchCareSpecialists(): Promise<GraphQLClientResponse<CareSpecialistsResponse>> {
    this.requireAuth();

    try {
      const result = await usersGraphqlService.getCareSpecialists(this.context.hasuraToken);
      return result as unknown as GraphQLClientResponse<CareSpecialistsResponse>;
    } catch (error) {
      console.error('Error fetching care specialists:', error);
      throw new Error('Failed to fetch care specialists');
    }
  }

  /**
   * Fetch care specialist status (deferred task and follow up flags)
   */
  async fetchCareSpecialistStatus(careSpecialistId: number): Promise<GraphQLClientResponse<CareSpecialistStatusResponse>> {
    this.requireAuth();

    if (!careSpecialistId) {
      throw new Error('Care Specialist ID is required');
    }

    try {
      const result = await usersGraphqlService.getCareSpecialistStatus(this.context.hasuraToken, careSpecialistId);
      return result as unknown as GraphQLClientResponse<CareSpecialistStatusResponse>;
    } catch (error) {
      console.error('Error fetching care specialist status:', error);
      throw new Error('Failed to fetch care specialist status');
    }
  }

  /**
   * Fetch care specialist clinic assignments
   */
  async fetchCareSpecialistAssignments(careSpecialistId: number): Promise<GraphQLClientResponse<CareSpecialistAssignmentsResponse>> {
    this.requireAuth();

    if (!careSpecialistId) {
      throw new Error('Care Specialist ID is required');
    }

    try {
      const result = await usersGraphqlService.getCareSpecialistAssignments(this.context.hasuraToken, careSpecialistId);
      return result as unknown as GraphQLClientResponse<CareSpecialistAssignmentsResponse>;
    } catch (error) {
      console.error('Error fetching care specialist assignments:', error);
      throw new Error('Failed to fetch care specialist assignments');
    }
  }

  /**
   * Filter care specialists by search term
   */
  filterCareSpecialists(specialists: CareSpecialist[], searchTerm: string): CareSpecialist[] {
    if (!searchTerm) return specialists;

    const searchTermLower = searchTerm.toLowerCase();

    return specialists.filter((specialist: CareSpecialist) =>
      specialist.full_name?.toLowerCase().includes(searchTermLower) ||
      specialist.first_name?.toLowerCase().includes(searchTermLower) ||
      specialist.last_name?.toLowerCase().includes(searchTermLower) ||
      specialist.email?.toLowerCase().includes(searchTermLower) ||
      specialist.clinic_names?.toLowerCase().includes(searchTermLower)
    );
  }

  /**
   * Sort care specialists by specified field
   */
  sortCareSpecialists(specialists: CareSpecialist[], sortBy: keyof CareSpecialist, sortOrder: 'asc' | 'desc' = 'asc'): CareSpecialist[] {
    return [...specialists].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }

  /**
   * Find care specialist by ID
   */
  findCareSpecialistById(specialists: CareSpecialist[], id: number): CareSpecialist | undefined {
    return specialists.find(specialist => specialist.care_specialist_id === id);
  }

  /**
   * Get care specialists by clinic ID
   */
  getCareSpecialistsByClinic(specialists: CareSpecialist[], clinicId: number): CareSpecialist[] {
    return specialists.filter(specialist =>
      Array.isArray(specialist.clinic_ids) && specialist.clinic_ids.includes(clinicId)
    );
  }

  /**
   * Process clinic IDs from assignments response
   */
  processClinicIds(assignments: Array<{ clinic_ids: number[] }>): number[] {
    const allClinicIds: number[] = assignments.flatMap((assignment) => assignment.clinic_ids || []);
    return [...new Set(allClinicIds)]; // Remove duplicates
  }

  /**
   * Get care specialist options for dropdowns
   */
  getCareSpecialistOptions(specialists: CareSpecialist[]): Array<{ value: number; label: string }> {
    return specialists.map(specialist => ({
      value: specialist.care_specialist_id,
      label: specialist.full_name || `${specialist.first_name} ${specialist.last_name}`.trim()
    }));
  }

  /**
   * Create a new care specialist
   */
  async createCareSpecialist(payload: CreateCareSpecialistPayload): Promise<CreateCareSpecialistResponse> {
    this.requireAuth();
    this.requireUserId();

    try {
      const response = await this.apiClient.post(`/api/pep/${this.context.userId}/ca`, payload, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Care specialist created successfully'
      };
    } catch (error) {
      console.error('Error creating care specialist:', error);
      throw error;
    }
  }

  /**
   * Delete a care specialist
   */
  async deleteCareSpecialist(careSpecialistId: number): Promise<CreateCareSpecialistResponse> {
    this.requireAuth();
    this.requireUserId();

    if (!careSpecialistId) {
      throw new Error('Care Specialist ID is required');
    }

    try {
      const response = await this.apiClient.delete(`/api/pep/${this.context.userId}/ca/${careSpecialistId}`, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Care specialist deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting care specialist:', error);
      throw error;
    }
  }

  /**
   * Update an existing care specialist
   */
  async updateCareSpecialist(
    careSpecialistId: number,
    payload: UpdateCareSpecialistPayload
  ): Promise<UpdateCareSpecialistResponse> {
    this.requireAuth();
    this.requireUserId();

    if (!careSpecialistId) {
      throw new Error('Care Specialist ID is required');
    }

    try {
      const response = await this.apiClient.put(`/api/pep/${this.context.userId}/ca/${careSpecialistId}`, payload, this.context.token);
      return {
        success: true,
        data: response.data,
        message: 'Care specialist updated successfully'
      };
    } catch (error) {
      console.error('Error updating care specialist:', error);

      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          throw new Error("Authentication failed. Please check your credentials and try again.");
        } else if (error.message.includes('400')) {
          throw new Error("Invalid information provided. Please check all fields and try again.");
        } else if (error.message.includes('404')) {
          throw new Error("Care specialist not found.");
        } else if (error.message.includes('500')) {
          throw new Error("Server error. Please try again later.");
        }
      }

      throw error;
    }
  }

  /**
   * Fetch care specialist patient list
   */
  async fetchCareSpecialistPatientList(
    clinicIds: number[],
    viewType: 'main' | 'followup' | 'deferred' = 'main'
  ): Promise<GraphQLClientResponse<any>> {
    this.requireAuth();

    if (!clinicIds || clinicIds.length === 0) {
      throw new Error('Clinic IDs are required');
    }

    try {
      if (viewType === 'main') {
        // For main view, we'll use the direct GraphQL query since there's no specific method in usersGraphqlService
        return await this.graphqlClient.query<any>(
          `query GetCareSpecialistPatientList($clinicIds: [Int!]!) {
            pep_vw_cs_task_patient_list(
              where: {clinic_id: {_in: $clinicIds}},
              order_by: {consented_appointment_date_time: desc_nulls_last}
            ) {
              pep_patient_id
              patient_clinic_data_id
              full_name
              clinic_id
              clinic_name
              patient_clinic_timezone
              consented_appointment_date_time
              dob
              provider_name
              mobile_phone_number
              ccm_status_value
              rpm_status_value
            }
          }`,
          { clinicIds },
          this.context.hasuraToken
        );
      } else {
        // For followup and deferred views, we need to use a different approach
        // since the centralized query doesn't support dynamic where clauses yet
        let whereConditions = [];

        if (viewType === 'followup') {
          whereConditions.push('ccm_status_value: {_eq: "Follow Up"}');
        } else if (viewType === 'deferred') {
          whereConditions.push('ccm_status_value: {_eq: "Deferred (CS)"}');
        }

        const whereClause = whereConditions.length > 0 ? `where: {${whereConditions.join(', ')}}` : '';

        const query = `query MyQuery {
          pep_vw_cs_task_patient_list(
            ${whereClause},
            order_by: {consented_appointment_date_time: desc_nulls_last}
          ) {
            pep_patient_id
            patient_clinic_data_id
            full_name
            clinic_id
            clinic_name
            patient_clinic_timezone
            consented_appointment_date_time
            dob
            provider_name
            mobile_phone_number
            ccm_status_value
            rpm_status_value
          }
        }`;

        return await this.graphqlClient.query<any>(
          query,
          {},
          this.context.hasuraToken
        );
      }
    } catch (error) {
      console.error('Error fetching care specialist patient list:', error);
      throw new Error('Failed to fetch care specialist patient list');
    }
  }
}